/**
 * API Response Normalization Helpers
 * Handles different API response structures (GPX API vs Legacy)
 */

/**
 * Normalizes API response to standard format
 * @param {Object} response - Raw API response
 * @returns {Object} Normalized response { data, success, message }
 */
export const normalizeApiResponse = (response) => {
  if (!response) {
    return { data: null, success: false, message: 'No response' };
  }

  // GPX API format: { data, success, message }
  if (response.success !== undefined) {
    return {
      data: response.data,
      success: response.success,
      message: response.message || 'Success',
    };
  }

  // Legacy format: direct data
  return {
    data: response,
    success: true,
    message: 'Success',
  };
};

/**
 * Extracts data from normalized response
 * @param {Object} response - API response
 * @returns {*} Extracted data
 */
export const extractData = (response) => {
  const normalized = normalizeApiResponse(response);
  return normalized.data;
};

/**
 * Validates API response structure
 * @param {Object} response - API response
 * @returns {boolean} True if valid
 */
export const validateResponse = (response) => {
  if (!response) return false;

  const normalized = normalizeApiResponse(response);
  return normalized.success === true;
};

/**
 * Creates error response object
 * @param {Error} error - Error object
 * @returns {Object} Structured error
 */
export const createErrorResponse = (error) => {
  const errorData = error.response?.data;

  // GPX uses { errorCode, message }; F2W uses { code, error }.
  if (errorData?.errorCode || errorData?.code) {
    return {
      errorCode: errorData.errorCode || errorData.code,
      message: errorData.message || errorData.error || error.message || 'An error occurred',
      ...(errorData.traceId && { traceId: errorData.traceId }),
      ...(errorData.timestamp && { timestamp: errorData.timestamp }),
      success: false,
    };
  }

  // Legacy error format
  return {
    errorCode: 'UNKNOWN_ERROR',
    message: errorData?.message || errorData?.error || error.message || 'An error occurred',
    success: false,
  };
};

/**
 * Creates success response object
 * @param {*} data - Response data
 * @param {string} message - Success message
 * @returns {Object} Structured success response
 */
export const createSuccessResponse = (data, message = 'Success') => {
  return {
    data,
    success: true,
    message,
  };
};
